import axios from 'axios';

// Cloudflare Pages environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 600 seconds (10 minutes) for Cloudflare Workers
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', {
      email,
      password
    });
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  setupPassword: async (password, confirmPassword) => {
    const response = await api.post('/auth/setup-password', {
      password,
      confirmPassword
    });
    return response.data;
  },

  getPasswordSetupStatus: async () => {
    const response = await api.get('/auth/password-setup-status');
    return response.data;
  }
};

// Hospital API
export const hospitalAPI = {
  getAll: async () => {
    const response = await api.get('/hospitals');
    return response.data;
  },
  
  create: async (name, description = '') => {
    const response = await api.post('/hospitals', { name, description });
    return response.data;
  },

  update: async (id, name, description = '') => {
    const response = await api.put(`/hospitals/${id}`, { name, description });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/hospitals/${id}`);
    return response.data;
  }
};

// User API
export const userAPI = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/users/stats');
    return response.data;
  },

  deleteAccount: async () => {
    const response = await api.delete('/users/account');
    return response.data;
  }
};

// Anesthesia Types API
export const anesthesiaTypesAPI = {
  getAll: async () => {
    const response = await api.get('/anesthesia-types');
    return response.data;
  },

  create: async (name) => {
    const response = await api.post('/anesthesia-types', { name });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/anesthesia-types/${id}`);
    return response.data;
  }
};

// Service API
export const serviceAPI = {
  create: async (serviceData, file) => {
    const formData = new FormData();
    
    // Append all service data
    Object.keys(serviceData).forEach(key => {
      if (serviceData[key] !== null && serviceData[key] !== undefined && serviceData[key] !== '') {
        formData.append(key, serviceData[key]);
      }
    });
    
    // Append file if provided
    if (file) {
      formData.append('bill_file', file);
    }
    
    const response = await api.post('/services', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });
    
    const response = await api.get(`/services?${params.toString()}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/services/${id}`);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/services/${id}`);
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },

  getHospitalEarnings: async () => {
    const response = await api.get('/dashboard/hospital-earnings');
    return response.data;
  }
};

// Export API
export const exportAPI = {
  exportToExcel: async (filtersParam = '') => {
    try {
      // Handle both object and string parameters
      let queryString = '';
      if (typeof filtersParam === 'string') {
        queryString = filtersParam;
      } else if (typeof filtersParam === 'object' && filtersParam !== null) {
        const params = new URLSearchParams();
        Object.keys(filtersParam).forEach(key => {
          if (filtersParam[key] !== null && filtersParam[key] !== undefined && filtersParam[key] !== '') {
            params.append(key, filtersParam[key]);
          }
        });
        queryString = params.toString();
      }
      
      const url = `/export/excel${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url, {
        responseType: 'blob',
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'anesthetist_services.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Export error:', error);
      throw new Error('Failed to export data: ' + (error.response?.data?.detail || error.message));
    }
  }
};

export default api;